import { sortBy } from "lodash"
import type { NextRouter } from "next/router"
import type { ReactNode } from "react"
import React from "react"

import { Loading } from "@/lib/loading"
import type { Notices } from "@/lib/notices"
import { buildNotice } from "@/lib/notices/buildNotice"
import { NoticesContext } from "@/lib/notices/context"
import { processNoticesRouteChange } from "@/lib/notices/processRouteChange"
import { ReactPerformance } from "@/lib/react_performance"

type Props = {
  children: ReactNode
  loading: Loading.ContextData
  router: NextRouter
}

type State = {
  list: Notices.Item[]
  loadingConfig: Loading.Config
  update: boolean
}

export class NoticesContainerComponent extends React.Component<Props, State> {
  private collection: Notices.Collection = {}

  public state: State = {
    list: [],
    loadingConfig: {
      dependencies: [`routing`],
      processId: `notices`,
    },
    update: false,
  }

  public componentDidMount = () => {
    this.componentMaybeReady()
  }

  public componentDidUpdate = () => {
    this.componentMaybeReady()
  }

  private componentMaybeReady = Loading.curryOnMaybeReady(this, {
    onDependenciesResolved: () => {
      this.props.router.events.on(
        `routeChangeComplete`,
        this.onRouteChangeComplete
      )
    },
  })

  public componentWillUnmount = () => {
    this.props.router.events.off(
      `routeChangeComplete`,
      this.onRouteChangeComplete
    )
  }

  private clearNotices: Notices.Functions.Clear = () => {
    this.collection = {}
    this.updateState({
      list: [],
    })
  }

  private displayNotice: Notices.Functions.Display = (options) => {
    this.collection[options.id] = buildNotice(options)

    this.refreshNotices()
  }

  private onRouteChangeComplete = () => {
    const notices = Object.values(this.collection)

    if (notices.length < 1) {
      return
    }

    this.collection = processNoticesRouteChange({ notices })

    this.refreshNotices()
  }

  private refreshNotices = () => {
    this.updateState({
      list: sortBy(
        this.collection,
        `type`,
        `persistence.accruedRouteChanges`,
        `id`
      ),
    })
  }

  private removeNotice: Notices.Functions.Remove = ({ id }) => {
    const { [id]: _, ...collection } = this.collection

    this.collection = collection

    this.refreshNotices()
  }

  public render = (): ReactNode => {
    return (
      <NoticesContext.Provider
        value={{
          clearNotices: this.clearNotices,
          displayNotice: this.displayNotice,
          list: this.state.list,
          removeNotice: this.removeNotice,
        }}
      >
        {this.props.children}
      </NoticesContext.Provider>
    )
  }

  public shouldComponentUpdate: ReactPerformance.Functions.ShouldComponentUpdate<
    Props,
    State
  > = ReactPerformance.curryShouldUpdateWithManagedState(
    this,
    ({ nextProps, nextState }) => {
      if (Loading.shouldComponentReady(nextProps, nextState)) {
        return { update: true }
      }

      if (this.props.children !== nextProps.children) {
        return { update: true }
      }
    }
  )

  private updateState = ReactPerformance.curryUpdateManagedState(this)
}
